<template>
  <a-drawer
    :open="open"
    :width="820"
    title="설비 알림톡 추적 상세"
    placement="right"
    @close="$emit('close')"
  >
    <a-spin :spinning="loading">
      <template v-if="detail">
        <!-- 보증서 기본 정보 -->
        <a-descriptions size="small" bordered :column="2">
          <a-descriptions-item label="보증번호" :span="2">
            {{ detail.warrantyNo }}
          </a-descriptions-item>
          <a-descriptions-item label="회원사">
            {{ detail.mmbsNm || "-" }}
          </a-descriptions-item>
          <a-descriptions-item label="조합원번호">
            {{ detail.mmbsNo || "-" }}
          </a-descriptions-item>
          <a-descriptions-item label="계약건명" :span="2">
            {{ detail.ctrtThngNm || "-" }}
          </a-descriptions-item>
          <a-descriptions-item label="수신번호">
            {{ detail.phoneNumber || "-" }}
          </a-descriptions-item>
          <a-descriptions-item label="조합 승인상태">
            {{ detail.aprvStatus || "-" }}
          </a-descriptions-item>
        </a-descriptions>

        <!-- 스텝 타임라인 -->
        <div class="section">
          <div class="section-title">진행 스텝</div>
          <SeolbiStepTimeline :steps="detail.steps" />
        </div>

        <!-- 예외 분기 스텝 -->
        <div class="section" v-if="hasBranchActivity">
          <div class="section-title">예외 분기</div>
          <SeolbiStepTimeline :steps="detail.branchSteps" />
        </div>

        <!-- 문제 요약 -->
        <a-alert
          v-if="problemSteps.length > 0"
          class="section"
          type="warning"
          show-icon
        >
          <template #message>확인이 필요한 스텝 {{ problemSteps.length }}건</template>
          <template #description>
            <ul class="problem-list">
              <li v-for="step in problemSteps" :key="step.step">
                <strong>{{ step.label }}</strong>
                —
                <span>{{ describeProblem(step) }}</span>
              </li>
            </ul>
          </template>
        </a-alert>

        <!-- 알림톡 시도 원장 -->
        <div class="section">
          <div class="section-title">
            알림톡 시도 이력 ({{ detail.logs.length }}건)
          </div>

          <a-empty v-if="detail.logs.length === 0" description="시도 기록 없음" />

          <a-collapse v-else accordion>
            <a-collapse-panel
              v-for="log in detail.logs"
              :key="log._id"
              :header="headerOf(log)"
            >
              <a-descriptions size="small" bordered :column="2">
                <a-descriptions-item label="템플릿">
                  {{ log.templateCode || "-" }}
                </a-descriptions-item>
                <a-descriptions-item label="트리거">
                  <a-tag :color="triggerMeta(log.triggerSource).color">
                    {{ triggerMeta(log.triggerSource).label }}
                  </a-tag>
                </a-descriptions-item>
                <a-descriptions-item label="추적 ID" :span="2">
                  <code>{{ log.traceId }}</code>
                </a-descriptions-item>
                <a-descriptions-item
                  v-if="log.groupWarrantyNos && log.groupWarrantyNos.length > 0"
                  label="묶음 보증번호"
                  :span="2"
                >
                  {{ log.groupWarrantyNos.join(", ") }}
                </a-descriptions-item>
                <a-descriptions-item v-if="log.providerCid" label="BizMsg CID" :span="2">
                  <code>{{ log.providerCid }}</code>
                </a-descriptions-item>
                <a-descriptions-item v-if="log.consumedAt" label="컨슈머 수신">
                  {{ formatDate(log.consumedAt) }}
                </a-descriptions-item>
                <a-descriptions-item v-if="log.sentAt" label="발송 완료">
                  {{ formatDate(log.sentAt) }}
                </a-descriptions-item>
                <a-descriptions-item v-if="log.errorMessage" label="실패 사유" :span="2">
                  <span class="error-text">{{ log.errorMessage }}</span>
                </a-descriptions-item>
              </a-descriptions>

              <!-- phase별 처리 경로 — 어디서 끊겼는지 -->
              <div class="phase-title">처리 경로</div>
              <a-steps
                size="small"
                direction="vertical"
                :current="log.phases.length - 1"
              >
                <a-step
                  v-for="phase in log.phases"
                  :key="phase.name"
                  :status="phase.status === 'OK' ? 'finish' : 'error'"
                  :title="`${phaseMeta(phase.name).label} (${phase.durationMs ?? 0}ms)`"
                >
                  <template #description>
                    <div class="phase-desc">
                      {{ phaseMeta(phase.name).description }}
                    </div>
                    <div v-if="phase.error" class="error-text">
                      {{ phase.error }}
                    </div>
                    <div v-if="phase.detail" class="phase-detail">
                      {{ JSON.stringify(phase.detail) }}
                    </div>
                  </template>
                </a-step>
              </a-steps>

              <a-alert
                v-if="log.status === 'QUEUED'"
                class="queue-note"
                type="warning"
                show-icon
                message="mq 컨슈머가 아직 이 job을 가져가지 않았습니다."
                description="큐 적재는 끝났지만 컨슈머 수신(CONSUMED) 기록이 없습니다. 대기 시간이 길어지면 mq 리시버가 내려갔거나 큐를 소비하지 못하는 상태일 수 있습니다."
              />
              <a-alert
                v-else-if="log.status === 'CONSUMED'"
                class="queue-note"
                type="info"
                show-icon
                message="컨슈머가 job을 가져가 처리 중입니다."
                description="발송 결과(DISPATCHED)가 아직 기록되지 않았습니다."
              />
              <a-alert
                v-else-if="log.status === 'FAILED' && log.failedPhase === 'DISPATCHED'"
                class="queue-note"
                type="error"
                show-icon
                message="컨슈머는 정상적으로 읽었으나 카카오 발송에 실패했습니다."
                description="큐·리시버 문제가 아니라 BizMsg 호출 단계의 문제입니다. 아래 처리 경로의 마지막 단계를 확인하세요."
              />
            </a-collapse-panel>
          </a-collapse>
        </div>
      </template>
    </a-spin>
  </a-drawer>
</template>

<script>
import { computed } from "vue";
import SeolbiStepTimeline from "./SeolbiStepTimeline.vue";
import {
  ATTEMPT_STATUS_META,
  PHASE_META,
  STEP_STATUS_META,
  TRIGGER_SOURCE_META,
} from "../seolbiTrackingApi";

export default {
  name: "SeolbiTrackingDetailDrawer",
  components: { SeolbiStepTimeline },
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    detail: {
      type: Object,
      default: null,
    },
  },
  emits: ["close"],
  setup(props) {
    // IN_FLIGHT는 정상 처리 중이므로 문제로 보지 않습니다.
    const PROBLEM_STATUSES = ["FAILED", "MISSING", "STALLED"];

    const problemSteps = computed(() => {
      if (!props.detail) return [];

      return [...props.detail.steps, ...props.detail.branchSteps].filter((step) =>
        PROBLEM_STATUSES.includes(step.status),
      );
    });

    const describeProblem = (step) => {
      if (step.error) {
        return step.error;
      }

      return (
        STEP_STATUS_META[step.status]?.description ?? "확인이 필요한 상태입니다."
      );
    };

    // 환불/취소는 발생한 적 있을 때만 보여줍니다.
    const hasBranchActivity = computed(() => {
      if (!props.detail) return false;

      return props.detail.branchSteps.some((step) => step.status !== "PENDING");
    });

    const phaseMeta = (name) =>
      PHASE_META[name] ?? { label: name, description: "" };

    const triggerMeta = (source) =>
      TRIGGER_SOURCE_META[source] ?? { label: source, color: "default" };

    const formatDate = (value) =>
      value ? new Date(value).toLocaleString("ko-KR") : "-";

    const headerOf = (log) => {
      const status = ATTEMPT_STATUS_META[log.status]?.label ?? log.status;

      return `${log.step} · ${status} · ${formatDate(log.createdAt)}`;
    };

    return {
      problemSteps,
      hasBranchActivity,
      describeProblem,
      phaseMeta,
      triggerMeta,
      headerOf,
      formatDate,
    };
  },
};
</script>

<style scoped>
.section {
  margin-top: 24px;
}

.section-title {
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 600;
  color: #262626;
}

.problem-list {
  margin: 0;
  padding-left: 18px;
}

.phase-title {
  margin: 16px 0 8px;
  font-size: 13px;
  font-weight: 600;
  color: #595959;
}

.phase-desc {
  font-size: 12px;
  color: #8c8c8c;
}

.phase-detail {
  margin-top: 4px;
  font-size: 11px;
  color: #8c8c8c;
  word-break: break-all;
}

.error-text {
  color: #f5222d;
  font-size: 12px;
}

.queue-note {
  margin-top: 16px;
}
</style>
